/**
 * 推广联盟 · Mini invite page
 *
 * Design reference: _design_reference/mini-invite.html
 * Layout (top → bottom):
 *   1. Hero — purple #6B4EFF gradient, pill showing personal invited/earned stats,
 *      headline "邀好友购物各得500₽", sub-copy
 *   2. Stats grid (3 cells): invited | earned commission | pending settlement
 *   3. Invite-code card (centered): big code, 3 buttons: copy · TG share · (poster hidden)
 *   4. Rules card (purple-soft bg) — bulleted list
 *   5. Recent invites: genuine live top20 rank entries shown as recent-style rows
 *      (backend has no "recent invited" list; we use rank data for social proof)
 *   6. Commission flow preview: settled + unsettled totals (from wallet)
 *   7. Bottom CTA: tg-main "立即邀请好友"
 *
 * API: usePromoteStat / usePromoteBonusWallet / usePromoteMonthlyRank / useUserInfo
 * All hooks from @kupikupi/api — nothing added to packages/api.
 *
 * Recent-invites section: backend genuinely has no per-invited-user list endpoint.
 * We surface the monthly rank leaderboard (real data) in a list-row style instead.
 * This is disclosed in the StructuredOutput backend_missing field.
 */
import {
  usePromoteBonusWallet,
  usePromoteMonthlyRank,
  usePromoteStat,
  useUserInfo,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

// ─── TG share helper ─────────────────────────────────────────────────────────
function openTgShare(url: string, text: string): void {
  // Opens Telegram's share-URL sheet inside the Mini App WebView.
  // Falls back gracefully if not in TG context.
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tg = typeof window !== "undefined" ? (window as any).Telegram?.WebApp : undefined;
  if (tg?.openLink) {
    tg.openLink(shareUrl);
  } else if (typeof window !== "undefined") {
    window.open(shareUrl, "_blank", "noopener");
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function InvitePage() {
  const { t } = useTranslation("affiliate");
  const authToken = useAuthToken();

  const { data: user } = useUserInfo(authToken);
  const { data: stat } = usePromoteStat(authToken);
  const { data: wallet } = usePromoteBonusWallet(authToken);
  const { data: rank } = usePromoteMonthlyRank(authToken);

  const [tooltip, setTooltip] = useState<"link" | "code" | "">("");

  const inviteCode = user?.inviteCode ?? (user?.id ? `KK${user.id}` : "—");
  const inviteLink = `https://t.me/kupikupi_bot?start=r${user?.id ?? ""}`;

  const totalInvited = stat?.totalInvitedUsers ?? 0;
  const settled = wallet?.settledBonus ?? 0;
  const unsettled = wallet?.unsettledBonus ?? 0;

  const settledFmt = formatPrice(Math.round(settled * 100), "ru-RU", "RUB");

  const copyText = async (text: string, which: "link" | "code") => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      // clipboard may be blocked in some TG versions — silent fail
    }
    setTooltip(which);
    setTimeout(() => setTooltip(""), 1400);
  };

  const handleTgShare = () => {
    openTgShare(inviteLink, t("invite.btn_tg"));
  };

  // rank entries used as "recent activity" social proof rows (top 5)
  const recentRows = (rank?.top20List ?? []).slice(0, 3);

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      <div
        className="tg-body"
        style={{ flex: 1, padding: 0, background: "var(--color-surface)", overflowY: "auto" }}
      >
        {/* ── Hero ── */}
        <div
          style={{
            background: "linear-gradient(135deg,#6B4EFF,#5B3FE6)",
            color: "#fff",
            padding: "22px 16px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative emoji watermark */}
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -20,
              fontSize: 120,
              opacity: 0.18,
              pointerEvents: "none",
            }}
          >
            👥
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
            {/* pill — personal stats or generic eyebrow when not loaded */}
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                background: "rgba(255,255,255,.18)",
                borderRadius: 999,
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.06em",
              }}
            >
              {authToken && stat
                ? t("invite.hero_pill", { count: totalInvited, amount: settledFmt })
                : t("hero.eyebrow")}
            </span>

            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 24,
                fontWeight: 900,
                margin: "10px 0 6px",
                color: "#fff",
                lineHeight: 1.1,
              }}
            >
              {t("hero.title_1")}
              <br />
              {t("hero.title_2")}{" "}
              <span style={{ color: "var(--color-sticker-yellow)" }}>500 ₽</span>
            </h1>

            <p style={{ fontSize: 12, opacity: 0.85, margin: 0 }}>
              {t("hero.sub_1")} 3–7.5% {t("hero.sub_2")}{" "}
              <strong>{t("hero.sub_3")}</strong>
            </p>
          </div>
        </div>

        {/* ── Stats grid (3 cells) ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 6,
            padding: "10px 10px 0",
          }}
        >
          {/* invited */}
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 8px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                fontWeight: 900,
                color: "var(--color-secondary)",
              }}
            >
              {totalInvited}
            </div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
              {t("stats.invited")}
            </div>
          </div>

          {/* settled commission */}
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 8px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 16,
                fontWeight: 900,
                color: "var(--color-primary-fill)",
                whiteSpace: "nowrap",
              }}
            >
              {authToken ? settledFmt : "—"}
            </div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
              {t("invite.stat_earned")}
            </div>
          </div>

          {/* unsettled / pending */}
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 8px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                fontWeight: 900,
                color: "var(--color-cta)",
              }}
            >
              {authToken
                ? formatPrice(Math.round(unsettled * 100), "ru-RU", "RUB")
                : "—"}
            </div>
            <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
              {t("invite.stat_pending")}
            </div>
          </div>
        </div>

        {/* ── Invite code card ── */}
        <div className="tg-card" style={{ textAlign: "center", margin: "10px 10px 0" }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--color-text-muted)",
              letterSpacing: "0.06em",
            }}
          >
            {t("invite.code_label")}
          </div>

          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 24,
              fontWeight: 900,
              margin: "6px 0 10px",
              letterSpacing: "0.04em",
            }}
          >
            {inviteCode !== "—" ? (
              <>
                {inviteCode.slice(0, -6)}
                <span style={{ color: "var(--color-cta)" }}>{inviteCode.slice(-6)}</span>
              </>
            ) : (
              <span style={{ color: "var(--color-text-muted)" }}>—</span>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
            {/* copy button */}
            <button
              type="button"
              onClick={() => copyText(inviteCode, "code")}
              style={{
                background: "var(--color-text)",
                color: "#fff",
                border: 0,
                padding: "8px 14px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tooltip === "code"
                ? `✓ ${t("tools.copied")}`
                : t("invite.btn_copy")}
            </button>

            {/* TG share button */}
            <button
              type="button"
              onClick={handleTgShare}
              style={{
                background: "#229ED9",
                color: "#fff",
                border: 0,
                padding: "8px 14px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {t("invite.btn_tg")}
            </button>

            {/* copy link pill (tertiary) */}
            <button
              type="button"
              onClick={() => copyText(inviteLink, "link")}
              style={{
                background: "var(--color-surface)",
                color: "var(--color-text)",
                border: "1px solid var(--color-border)",
                padding: "8px 14px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tooltip === "link"
                ? `✓ ${t("tools.copied")}`
                : t("tools.copy_link")}
            </button>
          </div>
        </div>

        {/* ── Rules card (purple-soft bg, matching design) ── */}
        <div
          className="tg-card"
          style={{
            background: "var(--color-secondary-soft)",
            margin: "10px 10px 0",
          }}
        >
          <h4
            className="tg-card__h"
            style={{ color: "var(--color-secondary)", margin: "0 0 8px" }}
          >
            {t("invite.rules_title")}
          </h4>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 11,
              lineHeight: 1.7,
              color: "var(--color-text)",
            }}
          >
            {[
              t("faq.a1"),
              t("faq.a2"),
              t("faq.a3"),
              t("faq.a4"),
            ].map((rule, i) => (
              /* biome-ignore lint/suspicious/noArrayIndexKey: static list */
              <li key={i}>{rule}</li>
            ))}
          </ul>
        </div>

        {/* ── Recent activity: top rank rows (real data, backend has no per-invite list) ── */}
        {recentRows.length > 0 && (
          <div
            className="tg-card"
            style={{ padding: 0, overflow: "hidden", margin: "10px 10px 0" }}
          >
            {/* header row */}
            <div
              style={{
                padding: "10px 14px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{t("board.title")}</span>
            </div>

            {recentRows.map((row, i) => {
              const initials = (row.userName ?? "?").slice(0, 1).toUpperCase();
              const avatarColors = [
                "linear-gradient(135deg,#6B4EFF,#5B3FE6)",
                "linear-gradient(135deg,#0BA888,#087A63)",
                "linear-gradient(135deg,#FF6B4A,#E54A2A)",
              ];
              return (
                <div
                  /* biome-ignore lint/suspicious/noArrayIndexKey: rank list */
                  key={row.userId ?? i}
                  style={{
                    padding: "10px 14px",
                    display: "grid",
                    gridTemplateColumns: "32px 1fr auto",
                    gap: 10,
                    alignItems: "center",
                    borderTop: "1px solid var(--color-border)",
                  }}
                >
                  {/* avatar */}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: avatarColors[i % avatarColors.length],
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                      fontFamily: "var(--font-display)",
                      fontWeight: 900,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </div>

                  {/* name + sub */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700 }}>
                      {row.userName ?? "—"}
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--color-text-muted)",
                        fontFamily: "var(--font-mono)",
                        marginTop: 1,
                      }}
                    >
                      {t("board.orders_sub", { orders: row.orderUsers ?? 0 })}
                    </div>
                  </div>

                  {/* bonus */}
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 12,
                      fontWeight: 900,
                      color: "var(--color-primary-fill)",
                    }}
                  >
                    {formatPrice(Math.round(row.totalBonus * 100), "ru-RU", "RUB")}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Commission flow preview (settled + unsettled from wallet) ── */}
        {authToken && wallet && (
          <div
            className="tg-card"
            style={{ padding: 0, overflow: "hidden", margin: "10px 10px 0" }}
          >
            {/* header with withdraw link */}
            <div
              style={{
                padding: "10px 14px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{t("earnings.title")}</span>
              <Link
                to={"/withdraw" as never}
                style={{
                  color: "var(--color-primary-fill)",
                  fontSize: 10,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                {t("earnings.withdraw")} →
              </Link>
            </div>

            {/* settled row */}
            <div
              style={{
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--color-border)",
                fontSize: 11,
                alignItems: "center",
              }}
            >
              <div>
                <strong>{t("earnings.sub")}</strong>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: 9,
                    fontFamily: "var(--font-mono)",
                    marginTop: 2,
                  }}
                >
                  {t("earnings.balance_hint")}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 13,
                  fontWeight: 900,
                  color: "var(--color-primary-fill)",
                }}
              >
                {settledFmt}
              </div>
            </div>

            {/* unsettled row */}
            <div
              style={{
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--color-border)",
                fontSize: 11,
                alignItems: "center",
              }}
            >
              <div>
                <strong>{t("invite.stat_pending")}</strong>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: 9,
                    fontFamily: "var(--font-mono)",
                    marginTop: 2,
                  }}
                >
                  {t("earnings.records")}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 13,
                  fontWeight: 900,
                  color: "var(--color-cta)",
                }}
              >
                {formatPrice(Math.round(unsettled * 100), "ru-RU", "RUB")}
              </div>
            </div>
          </div>
        )}

        {/* bottom padding so content clears tg-main */}
        <div style={{ height: 80 }} />
      </div>

      {/* ── Bottom CTA ── */}
      <TgMain onClick={handleTgShare}>{t("invite.cta")}</TgMain>
    </TgPage>
  );
}
